import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BillingActionError,
  fetchUsageOverview,
  setMemberLimit,
  SURFACES,
  type BillingStatus,
  type PersonUsage,
  type UsageOverview,
} from '@/billing/api';
import { ChangePlanDialog } from '@/billing/change-plan';
import { useBilling } from '@/billing/context';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import billingCopy from '@/i18n/ns/billing';
import { PAYMENT_FAILED } from '@/billing/plan-summary';
import { isNativeShell } from '@/lib/shell';
import { useOptionalWorkspace } from '@/workspace/context';
import { WorkspaceIcon } from '@/workspace/icons';
import { useIntlLocale } from '@/workspace/intl-locale';
import { BalanceCard } from './balance-card';
import { useUsageCopy, type UsageCopy } from './copy';
import { addDays, credits, formatters, isoDay, shares, todayIso, type Formatters } from './format';
import { PersonSummary } from './person-summary';
import { PeopleCard, peopleChart, ServiceCard } from './spend-cards';
import { Toolbar, type Range } from './toolbar';
import type { PersonView } from './people-list';
import { UsageOperations } from './operations';
import './usage.css';

const EMPTY_DAYS = new Map<string, UsageOverview['days']>();
const PERSON_COLORS = [
  'var(--cu-u1)',
  'var(--cu-u2)',
  'var(--cu-u3)',
  'var(--cu-u4)',
  'var(--cu-u5)',
];

type Load =
  | { state: 'loading' }
  | { state: 'error'; forbidden: boolean }
  | { state: 'ready'; overview: UsageOverview; at: Date };

function presets(copy: UsageCopy, today: string, overview: UsageOverview | null) {
  const list = [
    { label: copy.last7, range: { from: addDays(today, -6), to: today } },
    { label: copy.last30, range: { from: addDays(today, -29), to: today } },
    { label: copy.last90, range: { from: addDays(today, -89), to: today } },
  ];
  const start = overview?.account.periodStart;
  if (start && isoDay(new Date(start)) <= today)
    list.splice(2, 0, {
      label: copy.thisPeriod,
      range: { from: isoDay(new Date(start)), to: today },
    });
  return list;
}

/** Person rows with their colour, share and daily average for the range. */
function personViews(overview: UsageOverview): PersonView[] {
  const people = overview.people;
  const share = shares(people.map((person) => person.creditMicros));
  const n = Math.max(1, overview.days.length);
  let slot = 0;
  return people.map((person, i) => ({
    person,
    color:
      person.creditMicros > 0 && slot < PERSON_COLORS.length
        ? PERSON_COLORS[slot++]
        : 'var(--cu-u-other)',
    share: share[i],
    avg: credits(person.creditMicros) / n,
  }));
}

/** The days at the end of the range when the balance was spent, while usage is paused for it. */
function outOfCreditsFrom(overview: UsageOverview, paused: boolean) {
  if (!paused) return -1;
  let index = overview.balance.length;
  while (index > 0 && overview.balance[index - 1].balanceMicros <= 0) index--;
  return index < overview.balance.length ? index : -1;
}

function usePersonDays(load: Load, selectedId: string | null) {
  const overview = load.state === 'ready' ? load.overview : null;
  const [cache, setCache] = useState<{
    key: UsageOverview | null;
    days: Map<string, UsageOverview['days']>;
  }>({
    key: null,
    days: new Map(),
  });
  const days = cache.key === overview ? cache.days : EMPTY_DAYS;
  useEffect(() => {
    if (!overview || overview.scope !== 'company') return;
    const wanted = selectedId
      ? [selectedId]
      : overview.people
          .filter((person) => person.creditMicros > 0)
          .slice(0, 5)
          .map((person) => person.userId);
    const missing = wanted.filter((userId) => !days.has(userId));
    if (!missing.length) return;
    const controller = new AbortController();
    void Promise.all(
      missing.map((userId) =>
        fetchUsageOverview(
          { from: overview.from, to: overview.to, userId },
          controller.signal,
        ).then(
          (value) => [userId, value.days] as const,
          () => null,
        ),
      ),
    ).then((entries) => {
      if (controller.signal.aborted) return;
      setCache((current) => {
        const next = new Map(current.key === overview ? current.days : undefined);
        for (const entry of entries) if (entry) next.set(entry[0], entry[1]);
        return { key: overview, days: next };
      });
    });
    return () => controller.abort();
  }, [overview, selectedId, days]);
  return days;
}

function Heading({
  copy,
  personal,
  refreshing,
  onRefresh,
}: {
  copy: UsageCopy;
  personal: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  return (
    <header className="cu-heading">
      <div>
        <p className="cu-eyebrow">{copy.eyebrow}</p>
        <h1 className="cu-h1">{copy.title}</h1>
        <p className="cu-intro">{personal ? copy.introPersonal : copy.intro}</p>
      </div>
      <div className="cu-heading-actions">
        <button
          type="button"
          className="cu-btn"
          aria-label={copy.refresh}
          onClick={onRefresh}
          disabled={refreshing}
        >
          <WorkspaceIcon name="history" size={15} className={refreshing ? 'cu-spin' : undefined} />
          <span className="cu-refresh-label">{copy.refresh}</span>
        </button>
      </div>
    </header>
  );
}

function Stateful({ load, copy, onRetry }: { load: Load; copy: UsageCopy; onRetry: () => void }) {
  if (load.state === 'loading')
    return (
      <div className="ws-loading" role="status">
        <span className="ws-skeleton" />
        <span className="ws-skeleton" />
        <span className="ws-skeleton" />
        <p>{copy.loading}</p>
      </div>
    );
  if (load.state === 'error')
    return (
      <div className="ws-error" role="alert">
        <p>{load.forbidden ? copy.forbidden : copy.unavailable}</p>
        {!load.forbidden && (
          <button type="button" className="ws-text-button" onClick={onRetry}>
            {copy.tryAgain} <WorkspaceIcon name="arrow" size={14} />
          </button>
        )}
      </div>
    );
  return null;
}

function useUsageLoad(range: Range, reload: number) {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  useEffect(() => {
    const controller = new AbortController();
    setLoad((current) => (current.state === 'ready' ? current : { state: 'loading' }));
    fetchUsageOverview({ from: range.from, to: range.to }, controller.signal).then(
      (overview) => {
        if (!controller.signal.aborted) setLoad({ state: 'ready', overview, at: new Date() });
      },
      (error) => {
        if (controller.signal.aborted) return;
        setLoad({
          state: 'error',
          forbidden: error instanceof BillingActionError && error.status === 403,
        });
      },
    );
    return () => controller.abort();
  }, [range.from, range.to, reload]);
  return load;
}

function useToast() {
  const workspace = useOptionalWorkspace();
  const [text, setText] = useState('');
  useEffect(() => {
    if (!text) return;
    const timer = setTimeout(() => setText(''), 4200);
    return () => clearTimeout(timer);
  }, [text]);
  const show = useCallback(
    (value: string, tone: 'success' | 'error' = 'success') => {
      if (workspace) workspace.notify(value, null, tone);
      else setText(value);
    },
    [workspace],
  );
  return { text, show };
}

function updatedLabel(copy: UsageCopy, f: Formatters, at: Date | null, now: Date) {
  if (!at) return '';
  return now.getTime() - at.getTime() < 60_000
    ? copy.updatedNow
    : formatCopy(copy.updated, { time: f.time(at.toISOString()) });
}

export function UsagePage() {
  const copy = useUsageCopy();
  const locale = useIntlLocale();
  const f = useMemo(() => formatters(locale), [locale]);
  const billing = useBilling();
  const company = useCompany();
  const status = billing?.status ?? null;
  const today = todayIso();
  const [range, setRange] = useState<Range>({ from: addDays(today, -29), to: today });
  const [reload, setReload] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState(false);
  const load = useUsageLoad(range, reload);
  const personDays = usePersonDays(load, selectedId);
  const toast = useToast();
  const refresh = useCallback(() => {
    setReload((value) => value + 1);
    void billing?.refresh?.();
  }, [billing]);
  const overview = load.state === 'ready' ? load.overview : null;
  const personal = overview ? overview.scope !== 'company' : status?.canViewCompany === false;
  const native = isNativeShell();
  const currentUserId = company.data?.membership?.userId ?? null;

  const saveLimit = async (person: PersonUsage, value: number | null) => {
    try {
      await setMemberLimit(person.userId, value);
      toast.show(
        value === null
          ? formatCopy(copy.limitRemoved, { name: person.name })
          : formatCopy(copy.limitSaved, { name: person.name, limit: f.int(value) }),
      );
      setEditingId(null);
      refresh();
      return true;
    } catch {
      toast.show(copy.limitFailed, 'error');
      return false;
    }
  };

  return (
    <div className="cu-page" data-testid="usage-page">
      <Heading
        copy={copy}
        personal={personal}
        refreshing={load.state === 'loading'}
        onRefresh={refresh}
      />
      <Toolbar
        range={range}
        today={today}
        presets={presets(copy, today, overview)}
        people={overview?.scope === 'company' ? overview.people : []}
        colorOf={(userId) =>
          (overview ? personViews(overview) : []).find((view) => view.person.userId === userId)
            ?.color ?? 'var(--cu-u-other)'
        }
        selectedId={selectedId}
        showPeople={overview?.scope === 'company'}
        updated={updatedLabel(copy, f, load.state === 'ready' ? load.at : null, new Date())}
        f={f}
        copy={copy}
        onRange={(next) => {
          setRange(next);
          setEditingId(null);
        }}
        onPerson={(userId) => setSelectedId(userId)}
      />
      {overview ? (
        <Content
          overview={overview}
          status={status}
          selectedId={selectedId}
          editingId={editingId}
          currentUserId={currentUserId}
          personDays={personDays}
          native={native}
          f={f}
          copy={copy}
          onSelect={(userId) => {
            setSelectedId((current) => (current === userId ? null : userId));
            setEditingId(null);
          }}
          onEdit={setEditingId}
          onSave={saveLimit}
          onCancel={() => setEditingId(null)}
          onChangePlan={() => setDialog(true)}
        />
      ) : (
        <Stateful load={load} copy={copy} onRetry={refresh} />
      )}
      {status && (
        <ChangePlanDialog
          open={dialog}
          onOpenChange={setDialog}
          status={status}
          onChanged={() => {
            refresh();
          }}
        />
      )}
      {toast.text && (
        <div className="cu-toast" role="status">
          <WorkspaceIcon name="check" size={15} />
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}

interface ContentProps {
  overview: UsageOverview;
  status: BillingStatus | null;
  selectedId: string | null;
  editingId: string | null;
  currentUserId: string | null;
  personDays: Map<string, UsageOverview['days']>;
  native: boolean;
  f: Formatters;
  copy: UsageCopy;
  onSelect: (userId: string) => void;
  onEdit: (userId: string) => void;
  onSave: (person: PersonUsage, value: number | null) => Promise<boolean>;
  onCancel: () => void;
  onChangePlan: () => void;
}

function Content(props: ContentProps) {
  const { overview, status, selectedId, editingId, currentUserId, personDays, native, f, copy } =
    props;
  const days = overview.days.map((entry) => entry.day);
  const spend = overview.days.map((entry) =>
    SURFACES.reduce((sum, surface) => sum + credits(entry[surface]), 0),
  );
  const today = todayIso();
  const company = overview.scope === 'company';
  const views = personViews(overview);
  const selected = company
    ? (views.find((view) => view.person.userId === selectedId) ?? null)
    : null;
  const outFrom = outOfCreditsFrom(overview, status?.blockedReason === 'credits_exhausted');
  const own = company ? null : (views[0] ?? null);
  const canManage = Boolean(status?.canManage && overview.canManage);
  const billing = useNamespaceCopy(billingCopy);
  const pastDue =
    status && PAYMENT_FAILED.has(status.status)
      ? status.canManage
        ? billing.pausedPastDue
        : billing.pausedPastDueMember
      : null;
  return (
    <div className="cu-stack">
      {selected && (
        <PersonSummary
          person={selected.person}
          color={selected.color}
          share={selected.share}
          you={selected.person.userId === currentUserId}
          canManage={canManage}
          copy={copy}
          f={f}
          onEdit={() => props.onEdit(selected.person.userId)}
          onClear={() => props.onSelect(selected.person.userId)}
        />
      )}
      {own && (
        <PersonSummary
          person={own.person}
          color={own.color}
          share={null}
          you
          canManage={false}
          copy={copy}
          f={f}
        />
      )}
      <BalanceCard
        overview={overview}
        status={status}
        days={days}
        spend={spend}
        now={new Date()}
        canChangePlan={Boolean(status?.canManage && status.changePlanAvailable)}
        native={native}
        f={f}
        copy={copy}
        pastDue={pastDue}
        onChangePlan={props.onChangePlan}
      />
      <ServiceCard
        overview={overview}
        days={days}
        today={today}
        outFrom={outFrom}
        f={f}
        copy={copy}
      />
      <UsageOperations overview={overview} selectedId={selectedId} />
      {company ? (
        <PeopleCard
          views={views}
          chart={peopleChart({ copy, views, companyDays: overview.days, personDays, selected })}
          days={days}
          today={today}
          outFrom={outFrom}
          selected={selected}
          editingId={editingId}
          currentUserId={currentUserId}
          canManage={canManage}
          f={f}
          copy={copy}
          onSelect={props.onSelect}
          onEdit={props.onEdit}
          onSave={props.onSave}
          onCancel={props.onCancel}
        />
      ) : (
        <p className="cu-note is-plain">{copy.notePersonal}</p>
      )}
    </div>
  );
}
